import { Injectable } from '@angular/core';
import L from 'leaflet';

import { TramApiService } from '../tram-api.service';
import { LeafletRail, Rail, RailPoint, SegmentedLeafletRail } from '../core/rail';
import { Route } from '../core/route';
import { Coordinate } from '../core/coordinate';
import { LiteEvent } from './index';
import { EnhancedSegment } from '../core/segment';

@Injectable()
export class RailCollector {
    private readonly _onInitialized = new LiteEvent<LeafletRail[]>();
    private readonly _onBeforeSelect = new LiteEvent<any>();
    private readonly _onAfterSelect = new LiteEvent<any>();
    private readonly _onAfterSelectSegmentedRails = new LiteEvent<SegmentedLeafletRail[]>();

    private _rails: Map<number, LeafletRail>;
    get rails(): LeafletRail[] {
        let railsInternal: LeafletRail[] = [];

        if (this._rails !== undefined)
            this._rails.forEach(r => railsInternal.push(r));

        return railsInternal;
    }

    get afterInitialize() { return this._onInitialized.expose(); };
    onRailInitialized: (rail: LeafletRail) => void;
    get beforeSelect() { return this._onBeforeSelect.expose(); }
    get afterSelect() { return this._onAfterSelect.expose(); }
    get afterSelectSegmentedRails() { return this._onAfterSelectSegmentedRails.expose(); }

    constructor(private api: TramApiService) {
        this._rails = new Map<number, LeafletRail>();
    }

    init(canSelect: boolean) {
        this._rails.clear();

        return this.api.getRails(0, 0)
            .then(rails => {
                rails.forEach(r => {
                    let lRail = this.convertInternal(r, canSelect);
                    this._rails.set(r.id, lRail);

                    if (this.onRailInitialized)
                        this.onRailInitialized(lRail);
                });

                if (this._onInitialized)
                    this._onInitialized.trigger(this.rails);

                return this.rails;
            });
    }

    initByRoutes(routes: Route[]) {
        this._rails.clear();

        return this.api.getRails(0, 0)
            .then(rails => {
                let internalRails: Map<number, Rail> = new Map<number, Rail>();
                rails.forEach(r => internalRails.set(r.id, r));

                let allRailIds: number[] = [];
                routes.forEach(r => { allRailIds = allRailIds.concat(r.allRailIds); });
                allRailIds = Array.from(new Set(allRailIds));

                allRailIds.forEach(id => {
                    if (!internalRails.has(id))
                        return;

                    let rail = internalRails.get(id);

                    let lRail = this.convertInternal(rail, true);
                    this._rails.set(rail.id, lRail);

                    if (this.onRailInitialized)
                        this.onRailInitialized(lRail);
                });

                if (this._onInitialized) {
                    this._onInitialized.trigger(this.rails);
                }

                return this.rails;
            });
    }

    get(id: number): LeafletRail;
    get(segment: EnhancedSegment, isReverse: boolean): SegmentedLeafletRail[];
    get(...objects: any[]): any {
        if (objects[0] instanceof EnhancedSegment) {
            let segment = objects[0];
            let isReverse = objects[1];
            let stopIds = segment.stops.map(s => s.id);
            let segmentedRails: SegmentedLeafletRail[] = [];

            let rails = this.getRails(segment.route, isReverse);
            rails.forEach(r => {
                let filteredPoints = r.points.filter(p => p.stopId !== undefined);
                let pointStopIds = filteredPoints.map(p => p.stopId);
                let commonStopIds = this.intersect(pointStopIds, stopIds);
                if (commonStopIds.length) {
                    //take points from rail points based on 
                    let start = r.points.findIndex(p => p.stopId === commonStopIds[0]);
                    let finish = r.points.findIndex(p => p.stopId === commonStopIds[commonStopIds.length - 1]);

                    finish += 1; //to add top element to selection
                    let segmentedPoints = r.points.slice(start, finish);
                    if (segmentedPoints && segmentedPoints.length) {
                        let segmentedRail = new SegmentedLeafletRail(segmentedPoints);

                        segmentedRails.push(segmentedRail);

                        //remove common ids from stops
                        let stopStart = stopIds.findIndex(id => id === commonStopIds[0]);
                        let stopFinish = stopIds.findIndex(id => id === commonStopIds[commonStopIds.length - 1]);

                        stopFinish += 1; //to add top element to selection to remove it too
                        stopIds.splice(stopStart, stopFinish);
                    }
                }
            });

            return segmentedRails;
        } else {
            let id = objects[0];
            if (this._rails.has(id))
                return this._rails.get(id);
            return null;
        }
    }

    select(railId: number): void;
    select(route: Route, isReverse: boolean): void;
    select(segments: EnhancedSegment[]): void;
    select(...objects: any[]) {
        if (typeof (objects[0]) === 'number') {
            this.selectById(objects[0]);
        } else if (objects[0] instanceof Route) {
            this.selectByRoute(objects[0], objects[1]);
        } else if (objects[0] instanceof Array) {
            this.selectBySegments(objects[0]);
        }
    }

    selectGently(railId: number) {
        if (!this._rails.has(railId))
            return;

        let rail = this._rails.get(railId);
        rail.select();
    }

    unselect() {
        this.rails.forEach(r => {
            if (r.isSelected)
                r.unselect();
        });
    }

    enable(): void;
    enable(railId: number): void;
    enable(ids: number[]): void;
    enable(...objects: any[]) {
        if (objects.length === 0) {
            this._rails.forEach(r => r.enable());
        }
        else if (typeof (objects[0]) === 'number') {
            if (!this._rails.has(objects[0]))
                return;

            let rail = this._rails.get(objects[0]);
            rail.enable();
        } else if (objects[0] instanceof Array) {
            objects[0].forEach(id => {
                if (!this._rails.has(id))
                    return;

                let rail = this._rails.get(id);
                rail.enable();
            });
        }
    }

    disable(): void;
    disable(railId: number): void;
    disable(ids: number[]): void;
    disable(...objects: any[]) {
        if (objects.length === 0) {
            this._rails.forEach(r => r.disable());
        }
        else if (typeof (objects[0]) === 'number') {
            if (!this._rails.has(objects[0]))
                return;

            let rail = this._rails.get(objects[0]);
            if (rail.isSelected)
                rail.unselect();
            rail.disable();
        } else if (objects[0] instanceof Array) {
            objects[0].forEach(id => {
                if (!this._rails.has(id))
                    return;

                let rail = this._rails.get(id);
                if (rail.isSelected)
                    rail.unselect();
                rail.disable();
            })
        }
    }

    private convertInternal(rail: Rail, canSelect: boolean): LeafletRail {
        // let polylineBackground = L.polyline(rail.coordinates, this.optionsBackground);

        // let polyline = L.polyline(rail.coordinates, this.options);
        let lRail = new LeafletRail(rail);
        // lRail.polylineBackground = polylineBackground;
        // lRail.polyline = polyline;
        this._rails.set(lRail.id, lRail);

        if (canSelect)
            lRail.polyline.on('click', e => this.selectInternal(e, lRail));

        return lRail;
    }

    private selectById(id: number) {
        if (!this._rails.has(id))
            return;

        let rail = this._rails.get(id);
        this.selectInternal(null, rail);

    }
    private selectBySegments(segments: EnhancedSegment[]) {
        if (this._onAfterSelectSegmentedRails)
            this._onAfterSelectSegmentedRails.trigger(this.flatten(segments.map(s => s.rails)));
    }
    private selectByRoute(route: Route, isReverse: boolean) {
        let ids = isReverse
            ? route.reverseRailIds
            : route.railIds;

        ids.forEach(id => {
            if (!this._rails.has(id))
                return;

            let rail = this._rails.get(id);
            rail.select();
        });
    }

    private selectInternal(e: any, rail: LeafletRail) {
        if (this._onBeforeSelect)
            this._onBeforeSelect.trigger({ event: e, rail: rail });

        rail.select();

        if (this._onAfterSelect)
            this._onAfterSelect.trigger({ event: e, rail: rail });
    }

    private getRails(route: Route, isReverse: boolean): Rail[] {
        let rails: Rail[] = [];

        let ids = isReverse ? route.reverseRailIds : route.railIds;
        ids.forEach(id => {
            let rail = this.get(id);
            if (rail)
                rails.push(rail);
        });

        return rails;
    }
    private getAllRails(route: Route): Rail[] {
        let rails: Rail[] = [];

        route.allRailIds.forEach(id => {
            let rail = this.get(id);
            if (rail)
                rails.push(rail);
        });

        return rails;
    }

    private intersect(first_ids: number[], second_ids: number[]): number[] {
        let intersection = first_ids.filter(function (id) {
            return second_ids.indexOf(id) != -1;
        });

        return intersection;
    }

    private flatten(arr) {
        return arr.reduce(function (flat, toFlatten) {
            return flat.concat(Array.isArray(toFlatten) ? this.flatten(toFlatten) : toFlatten);
        }, []);
    }
}


// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/rail-collector.ts